import { initConnectedAppsI18next } from "@domains/connected-apps/client/i18n/i18next.ts";
import { ChartBarIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { hasAnyRole } from "@packages/core/session/roles.ts";
import type { Session } from "@packages/core/session/session.ts";
import type { AppModuleRegisterFunction } from "@packages/core-client/registration/appModuleRegisterFunction.ts";
import { registerCreateMenuItem } from "@packages/core-client/registration/createMenu.tsx";
import { ProtectedRoutes, PublicRoutes, type FireflyRuntime } from "@squide/firefly";
import type { i18n } from "i18next";
import { OFFICEVIBE_SURVEY_CREATE_ACCESS, PERFORMANCE_CYCLE_CREATE_ACCESS } from "#shared/access.ts";
import { NotFoundPage } from "./pages/NotFoundPage.tsx";
import { RootLayout } from "./layouts/RootLayout.tsx";
import { initI18next } from "./i18next.ts";

async function registerMsw(runtime: FireflyRuntime) {
    if (runtime.isMswEnabled) {
        const requestHandlers = (await import("./api/mocks/getRequestHandlers.ts")).getRequestHandlers();

        runtime.registerRequestHandlers(requestHandlers);

        const { failureRequestHandler } = await import("./api/mocks/failure/failureRequestHandler.ts");

        runtime.registerRequestHandlers([failureRequestHandler], { prepend: true });
    }
}

function registerRoutes(runtime: FireflyRuntime) {
    runtime.registerPublicRoute(
        {
            $id: "root-layout",
            element: <RootLayout />,
            children: [PublicRoutes]
        },
        {
            hoist: true
        }
    );

    runtime.registerRoute(
        {
            lazy: async () => {
                const { AuthenticationBoundary } = await import("./boundaries/AuthenticationBoundary.tsx");

                return {
                    element: <AuthenticationBoundary />
                };
            },
            children: [
                {
                    lazy: async () => {
                        const { AuthenticatedLayout } = await import("./layouts/AuthenticatedLayout.tsx");

                        return {
                            element: <AuthenticatedLayout />
                        };
                    },
                    children: [
                        {
                            lazy: async () => {
                                const { ModuleErrorBoundary } = await import("./boundaries/ModuleErrorBoundary.tsx");

                                return {
                                    errorElement: <ModuleErrorBoundary />
                                };
                            },
                            children: [
                                {
                                    lazy: async () => {
                                        const { AuthorizationBoundary } = await import("./boundaries/AuthorizationBoundary.tsx");

                                        return {
                                            element: <AuthorizationBoundary />
                                        };
                                    },
                                    children: [
                                        {
                                            lazy: async () => {
                                                const { AuthenticatedPageLayout } = await import("./layouts/AuthenticatedPageLayout.tsx");

                                                return {
                                                    element: <AuthenticatedPageLayout />
                                                };
                                            },
                                            children: [ProtectedRoutes]
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        },
        {
            parentId: "root-layout"
        }
    );

    runtime.registerPublicRoute({
        path: "*",
        element: <NotFoundPage />
    });
}

function registerCreateMenuRedirects(runtime: FireflyRuntime, i18nextInstance: i18n, session: Session | undefined) {
    const label = (resourceKey: string) => ({ instance: i18nextInstance, namespace: "CreateMenu", resourceKey });

    if (hasAnyRole(session, OFFICEVIBE_SURVEY_CREATE_ACCESS)) {
        registerCreateMenuItem(runtime, {
            id: "survey",
            label: label("newSurvey"),
            icon: ChartBarIcon,
            // oxlint-disable-next-line typescript/restrict-template-expressions -- Squide 19.2.1's class d.ts types getEnvironmentVariable() as never; the registry holds a string
            to: `${runtime.getEnvironmentVariable("officevibeUrl")}/portal/custom-polls/active/create/new`,
            priority: 20,
            isExternal: true
        });
    }

    if (hasAnyRole(session, PERFORMANCE_CYCLE_CREATE_ACCESS)) {
        const isBuilderEnabled = runtime.getFeatureFlag("review-cycle-builder", false);

        registerCreateMenuItem(runtime, {
            id: "review-cycle",
            label: label("newReviewCycle"),
            icon: ClipboardDocumentListIcon,
            to: isBuilderEnabled
                ? "/review-cycles/new"
                : // oxlint-disable-next-line typescript/restrict-template-expressions -- Squide 19.2.1's class d.ts types getEnvironmentVariable() as never; the registry holds a string
                  `${runtime.getEnvironmentVariable("workleapPerformanceUrl")}/app/review-cycles/create`,
            priority: 10,
            isExternal: !isBuilderEnabled
        });
    }
}

export const registerShell: AppModuleRegisterFunction = async runtime => {
    const i18nextInstance = initI18next(runtime);
    initConnectedAppsI18next(runtime);
    await registerMsw(runtime);
    registerRoutes(runtime);

    return (deferredRuntime, { session }) => registerCreateMenuRedirects(deferredRuntime, i18nextInstance, session);
};
